import { useState, useCallback } from "react";
import {
  useGetList,
  useCreate,
  useDelete,
  useNotify,
  useRefresh,
} from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, Loader2, Receipt } from "lucide-react";

interface Expense {
  id: string;
  job_id: string;
  org_id: string;
  category: "labor" | "material" | "subcontractor" | "other";
  description: string;
  vendor: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  expense_date: string;
  reference: string | null;
}

interface JobExpensesProps {
  jobId: string;
  readOnly?: boolean;
}

const CAT_COLORS: Record<string, { color: string; bg: string; label: string }> = {
  labor: { color: "#2563eb", bg: "#dbeafe", label: "L" },
  material: { color: "#16a34a", bg: "#dcfce7", label: "M" },
  subcontractor: { color: "#7c3aed", bg: "#ede9fe", label: "S" },
  other: { color: "#6b7280", bg: "#f3f4f6", label: "O" },
};

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

export function JobExpenses({ jobId, readOnly = false }: JobExpensesProps) {
  const { data, isPending } = useGetList<Expense>("job_expenses", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "expense_date", order: "DESC" },
    filter: { job_id: jobId },
  });

  const [create] = useCreate();
  const [deleteOne] = useDelete();
  const notify = useNotify();
  const refresh = useRefresh();
  const [adding, setAdding] = useState(false);

  const [newExp, setNewExp] = useState({
    category: "material" as Expense["category"],
    description: "",
    vendor: "",
    quantity: 1,
    unit_price: 0,
    expense_date: new Date().toISOString().split("T")[0],
    reference: "",
  });

  const handleAdd = useCallback(async () => {
    if (!newExp.description.trim()) {
      notify("Description is required", { type: "warning" });
      return;
    }
    setAdding(true);
    try {
      await create(
        "job_expenses",
        {
          data: {
            job_id: jobId,
            category: newExp.category,
            description: newExp.description.trim(),
            vendor: newExp.vendor.trim(),
            quantity: newExp.quantity,
            unit_price: newExp.unit_price,
            expense_date: newExp.expense_date,
            reference: newExp.reference.trim() || null,
          },
        },
        { returnPromise: true }
      );

      // Log event
      await create(
        "events",
        {
          data: {
            job_id: jobId,
            event_type: "job_expense_added",
            payload: {
              job_id: jobId,
              category: newExp.category,
              description: newExp.description.trim(),
              vendor: newExp.vendor.trim(),
              amount: newExp.quantity * newExp.unit_price,
            },
          },
        },
        { returnPromise: true }
      );

      setNewExp({
        category: "material",
        description: "",
        vendor: "",
        quantity: 1,
        unit_price: 0,
        expense_date: new Date().toISOString().split("T")[0],
        reference: "",
      });
      refresh();
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setAdding(false);
    }
  }, [newExp, jobId, create, notify, refresh]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteOne(
          "job_expenses",
          { id, previousData: { id } },
          { returnPromise: true }
        );
        refresh();
      } catch (err) {
        notify(`Delete failed: ${(err as Error).message}`, { type: "error" });
      }
    },
    [deleteOne, notify, refresh]
  );

  if (isPending) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        Loading expenses…
      </div>
    );
  }

  const items = data || [];

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold flex items-center gap-1.5">
        <Receipt className="w-4 h-4" />
        Expenses
        {items.length > 0 && (
          <span className="text-xs text-muted-foreground font-normal">
            ({items.length})
          </span>
        )}
      </h4>

      {/* Expense list */}
      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((exp) => {
            const cat = CAT_COLORS[exp.category] || CAT_COLORS.other;
            return (
              <Card key={exp.id} className="shadow-none">
                <CardContent className="p-2.5">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs font-bold w-5 h-5 flex items-center justify-center rounded shrink-0"
                      style={{ color: cat.color, backgroundColor: cat.bg }}
                    >
                      {cat.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {exp.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {exp.vendor && `${exp.vendor} · `}
                        {exp.expense_date}
                        {exp.reference && ` · ${exp.reference}`}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold tabular-nums">
                        {fmt(exp.line_total)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {exp.quantity} × {fmt(exp.unit_price)}
                      </p>
                    </div>
                    {!readOnly && (
                      <button
                        onClick={() => handleDelete(exp.id)}
                        className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {items.length === 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground">
          No expenses recorded yet.
        </div>
      )}

      {/* Add new expense */}
      {!readOnly && (
        <Card className="border-dashed">
          <CardContent className="p-3 space-y-2">
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground block mb-1">
                  Type
                </label>
                <select
                  value={newExp.category}
                  onChange={(e) =>
                    setNewExp((n) => ({
                      ...n,
                      category: e.target.value as Expense["category"],
                    }))
                  }
                  className="w-full px-1.5 py-1.5 border rounded text-xs"
                >
                  <option value="labor">Labor</option>
                  <option value="material">Material</option>
                  <option value="subcontractor">Subcontractor</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="col-span-3">
                <label className="text-xs text-muted-foreground block mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newExp.description}
                  onChange={(e) =>
                    setNewExp((n) => ({ ...n, description: e.target.value }))
                  }
                  placeholder="e.g. Lumber delivery"
                  className="w-full px-2 py-1.5 border rounded text-xs"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground block mb-1">
                  Vendor
                </label>
                <input
                  type="text"
                  value={newExp.vendor}
                  onChange={(e) =>
                    setNewExp((n) => ({ ...n, vendor: e.target.value }))
                  }
                  placeholder="Home Depot"
                  className="w-full px-2 py-1.5 border rounded text-xs"
                />
              </div>
              <div className="col-span-1">
                <label className="text-xs text-muted-foreground block mb-1">
                  Qty
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={newExp.quantity}
                  onChange={(e) =>
                    setNewExp((n) => ({
                      ...n,
                      quantity: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-full px-1 py-1.5 border rounded text-xs text-right"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground block mb-1">
                  Unit price
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={newExp.unit_price}
                  onChange={(e) =>
                    setNewExp((n) => ({
                      ...n,
                      unit_price: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-full px-1 py-1.5 border rounded text-xs text-right"
                />
              </div>
              <div className="col-span-2">
                <button
                  onClick={handleAdd}
                  disabled={adding || !newExp.description.trim()}
                  className="w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {adding ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Plus className="w-3 h-3" />
                  )}
                  Add
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
